import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPublishedPosts } from '../utils/blogService';
import LandingNav from '../components/LandingNav';
import '../pages/Landing.css';
import './Blog.css';

const COPY = {
  en: {
    titleBrand: 'NOVAS ',
    titleWord: 'blog',
    subtitle: 'Notes, ideas and updates about language teaching and the platform.',
    loading: 'Loading…',
    empty: 'No published posts yet — check back soon.',
    home: 'Home',
    contact: 'Contact us',
    rights: (year) => `© ${year} NOVAS. All rights reserved.`,
    dateLocale: 'en-US',
  },
  ru: {
    titleBrand: 'NOVAS ',
    titleWord: 'блог',
    subtitle: 'Заметки, идеи и новости о преподавании языков и платформе.',
    loading: 'Загрузка…',
    empty: 'Пока нет опубликованных постов — загляните позже.',
    home: 'На главную',
    contact: 'Связаться с нами',
    rights: (year) => `© ${year} NOVAS. Все права защищены.`,
    dateLocale: 'ru-RU',
  },
};

function formatDate(iso, locale) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch (_) {
    return '';
  }
}

export default function Blog() {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('novas-landing-lang') || 'en'; }
    catch (_) { return 'en'; }
  });
  const t = COPY[lang];
  const toggleLang = () => setLang((prev) => (prev === 'en' ? 'ru' : 'en'));

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Reuse the landing page's dark theme tokens (defined under
    // :root[data-landing-theme="dark"] in Landing.css) so the blog reads as
    // the same site rather than a visually disconnected page.
    document.documentElement.setAttribute('data-landing-theme', 'dark');
    return () => document.documentElement.removeAttribute('data-landing-theme');
  }, []);

  useEffect(() => {
    try { localStorage.setItem('novas-landing-lang', lang); } catch (_) {}
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPublishedPosts();
        if (!cancelled) setPosts(data);
      } catch (e) {
        console.error('Error loading blog posts:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="landing blog-page" id="top">
      <LandingNav lang={lang} toggleLang={toggleLang} />

      <div className="blog-orb blog-orb--a" aria-hidden />
      <div className="blog-orb blog-orb--b" aria-hidden />

      <header className="blog-list-header">
        <h1>{t.titleBrand}<span className="blog-title-word">{t.titleWord}</span></h1>
        <p>{t.subtitle}</p>
      </header>

      <main className="blog-list-main">
        {loading ? (
          <p className="blog-list-empty">{t.loading}</p>
        ) : posts.length === 0 ? (
          <p className="blog-list-empty">{t.empty}</p>
        ) : (
          <div className="blog-list-grid">
            {posts.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="blog-card">
                <div className="blog-card-cover">
                  {post.coverImageUrl ? (
                    <img src={post.coverImageUrl} alt={post.title} />
                  ) : (
                    <div className="blog-card-cover-placeholder" />
                  )}
                </div>
                <div className="blog-card-body">
                  <div className="blog-card-date">
                    {formatDate(post.publishedAt, t.dateLocale)}
                    <span className="blog-card-views">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
                        <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
                      </svg>
                      {post.views}
                    </span>
                  </div>
                  <h2>{post.title}</h2>
                  {post.excerpt && <p>{post.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <footer className="landing-footer">
        <div className="landing-footer-brand">{t.rights(new Date().getFullYear())}</div>
        <div className="landing-footer-links">
          <Link to="/">{t.home}</Link>
          <a href="mailto:novas.edtech@gmail.com">{t.contact}</a>
        </div>
      </footer>
    </div>
  );
}
